import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParallaxHero from '../components/ParallaxHero';
import { ArrowRight, ChevronDown, GraduationCap, Heart, UserCheck } from 'lucide-react';

const AboutUsPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [impactVisible, setImpactVisible] = useState(false);
  const [faqVisible, setFaqVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  
  const statsRef = useRef(null);
  const impactRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.15, rootMargin: '0px 0px -50px 0px' };
    
    const createObserver = (setVisible) => new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, observerOptions);

    const statsObserver = createObserver(setStatsVisible);
    const impactObserver = createObserver(setImpactVisible);
    const faqObserver = createObserver(setFaqVisible);
    const ctaObserver = createObserver(setCtaVisible);

    if (statsRef.current) statsObserver.observe(statsRef.current);
    if (impactRef.current) impactObserver.observe(impactRef.current);
    if (faqRef.current) faqObserver.observe(faqRef.current);
    if (ctaRef.current) ctaObserver.observe(ctaRef.current);

    return () => {
      statsObserver.disconnect();
      impactObserver.disconnect();
      faqObserver.disconnect();
      ctaObserver.disconnect();
    };
  }, []);

  // Warm Premium Palette - Consistent with Homepage
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4A7D78',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    bgWarmWhite: '#FFFDFB',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
    borderSage: '#C5D5CF',
    success: '#4D9078',
  };

  const stats = [
    { value: '₹500Cr+', label: 'CSR Capital Deployed' },
    { value: '200+', label: 'Enterprise Clients' },
    { value: '98.5%', label: 'Compliance Rate' },
    { value: '4.2M', label: 'Lives Impacted' }
  ];

  const impactAreas = [
    { icon: GraduationCap, title: 'Education', description: 'Skill development, digital literacy, and quality education initiatives across rural and urban India.' },
    { icon: Heart, title: 'Healthcare', description: 'Community health programs, medical camps, and wellness initiatives for underserved communities.' },
    { icon: UserCheck, title: 'Women Empowerment', description: 'Livelihood programs, entrepreneurship, and skill training for women across India.' }
  ];

  const faqs = [
    { question: 'How do I register my organization?', answer: 'Click "Register Now" and select your organization type. Fill in the required details and submit. Our team will review your application and verify your credentials before your listing goes live.' },
    { question: 'What due diligence do you conduct on NGOs?', answer: 'Every NGO undergoes comprehensive verification including 80G/12A registration, FCRA compliance (if applicable), financial health assessment, track record verification, and on-ground capacity evaluation.' },
    { question: 'How does the CSR matching process work?', answer: 'Once verified, our team analyzes your CSR policy, budget, and focus areas, then curates a shortlist of verified NGOs aligned with your requirements.' },
    { question: 'What compliance support do you provide?', answer: 'We handle CSR-2 form preparation, Schedule VII alignment documentation, impact assessment reports, and coordinate with your auditors.' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />

      {/* Hero with Parallax */}
      <ParallaxHero 
        backgroundColor={colors.primaryTeal}
        intensity={0.25}
        className="pt-32 pb-20 md:pt-40 md:pb-24 px-6"
      >
        <div className="max-w-6xl mx-auto text-center">
          <p 
            className="text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: colors.textOnDarkMuted }}
          >
            About CSR Exchange
          </p>
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight"
            style={{ color: colors.textOnDark }}
          >
            Transforming India&apos;s<br />CSR Landscape
          </h1>
          <p 
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            style={{ color: colors.textOnDarkMuted }}
          >
            Learn about our mission, impact areas, network, and how we&apos;re building a more sustainable future together.
          </p>
        </div>
      </ParallaxHero>

      {/* Stats Section */}
      <section 
        ref={statsRef}
        className="py-12" 
        style={{ backgroundColor: colors.bgSage, borderBottom: `1px solid ${colors.borderSage}` }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div 
                key={idx} 
                className={`text-center transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: statsVisible ? `${idx * 100}ms` : '0ms' }}
              >
                <div 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ color: colors.primaryBrown }}
                >
                  {stat.value}
                </div>
                <div 
                  className="text-sm font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section ref={impactRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.bgCream }}>
        <div className="max-w-6xl mx-auto">
          <div 
            className={`text-center mb-14 transition-all duration-700 ${impactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p 
              className="text-sm font-semibold uppercase tracking-widest mb-4"
              style={{ color: colors.textTertiary }}
            >
              Impact Areas
            </p>
            <h2 
              className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight"
              style={{ color: colors.textPrimary }}
            >
              Driving Change Across Sectors
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactAreas.map((area, idx) => (
              <div 
                key={idx}
                className={`rounded-2xl p-8 transition-all duration-500 group ${impactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bgCard, 
                  border: `1px solid ${colors.borderWarm}`,
                  transitionDelay: impactVisible ? `${200 + idx * 150}ms` : '0ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = colors.primaryTeal;
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.primaryTeal}, 0 12px 24px rgba(61, 107, 103, 0.1)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = colors.borderWarm;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-6"
                  style={{ backgroundColor: colors.bgSage }}
                >
                  <area.icon className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" style={{ color: colors.primaryTeal }} />
                </div>
                <h3 
                  className="text-lg font-semibold mb-3"
                  style={{ color: colors.textPrimary }}
                >
                  {area.title}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: colors.textSecondary }}
                >
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section ref={faqRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.bgSage }}>
        <div className="max-w-3xl mx-auto">
          <div 
            className={`text-center mb-14 transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p 
              className="text-sm font-semibold uppercase tracking-widest mb-4"
              style={{ color: colors.textTertiary }}
            >
              FAQs
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold tracking-tight"
              style={{ color: colors.textPrimary }}
            >
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className={`rounded-2xl overflow-hidden transition-all duration-500 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ 
                  backgroundColor: colors.bgCard, 
                  border: `1px solid ${colors.borderWarm}`,
                  transitionDelay: faqVisible ? `${200 + idx * 100}ms` : '0ms'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-200"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgCream}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span 
                    className="font-semibold pr-4"
                    style={{ color: colors.textPrimary }}
                  >
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${expandedFaq === idx ? 'rotate-180' : ''}`}
                    style={{ color: colors.primaryTeal }}
                  />
                </button>
                {expandedFaq === idx && (
                  <div 
                    className="px-6 pb-5 text-sm leading-relaxed"
                    style={{ color: colors.textSecondary }}
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: colors.primaryTeal }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-6 tracking-tight transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ color: colors.textOnDark }}
          >
            Ready to Make an Impact?
          </h2>
          <p 
            className={`text-lg mb-10 max-w-2xl mx-auto transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ color: colors.textOnDarkMuted, transitionDelay: ctaVisible ? '150ms' : '0ms' }}
          >
            Join India&apos;s leading CSR marketplace and connect with verified partners for meaningful, measurable impact.
          </p>
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: ctaVisible ? '300ms' : '0ms' }}
          >
            <button 
              onClick={() => navigate('/xchange/corporate/register')}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
              style={{ 
                backgroundColor: colors.primaryTeal,
                color: colors.textOnDark,
                boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)'
              }}
              data-testid="about-get-started-btn"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryTealLight}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primaryTeal}
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: colors.textOnDark,
                border: '1.5px solid rgba(255, 255, 255, 0.3)'
              }}
              data-testid="about-contact-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUsPage;
